import type { FC, PropsWithChildren } from 'react';
import { Suspense } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { MetaData, PageLayout } from '@xing-com/crate-entity-pages-common';
import { Appcues } from '@xing-com/crate-entity-pages-entity-pages-shared-appcues';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';
import { PageContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { ErrorMessage } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { EditorUrlParameter } from '@xing-com/crate-entity-pages-entity-pages-shared-policies-editor-url-parameter';
import { Header } from '@xing-com/crate-entity-pages-header';

import { subpagesConfig } from '../../config/subpages-config';
import { SubpageModule } from '../subpage-module/subpage-module';
import { SubpageTracking } from '../subpage-tracking/subpage-tracking';

import { SubpageLayout } from './subpage-layout';
import * as Styled from './subpage.styles';

interface SubpageContainerProps {
  pageSlug: string;
  pageType: string;
}

const SubpageContainerInner: FC<PropsWithChildren<SubpageContainerProps>> = ({
  pageSlug,
  pageType,
  children,
}) => {
  return (
    <PageContextProvider pageSlug={pageSlug} isSubpage={true}>
      <EditorUrlParameter>
        <SubpageTracking pageType={pageType}>
          <Appcues>
            <SubpageModule moduleType={pageType}>
              {(moduleGlobalId) => (
                <SubpageLayout
                  pageType={pageType}
                  moduleGlobalId={moduleGlobalId}
                >
                  {children}
                </SubpageLayout>
              )}
            </SubpageModule>
          </Appcues>
        </SubpageTracking>
      </EditorUrlParameter>
    </PageContextProvider>
  );
};

export const SubpageContainerFallback: FC<
  PropsWithChildren<SubpageContainerProps>
> = ({ pageType }) => {
  const seoIndexSubpage = subpagesConfig[pageType]?.seoIndexSubpage ?? true;
  return (
    <PageLayout>
      <Styled.PageWrapper>
        <MetaData setDefault={true} title={'XING'} index={seoIndexSubpage} />
        <Header.Skeleton />
      </Styled.PageWrapper>
    </PageLayout>
  );
};

export const SubpageContainer: FC<PropsWithChildren<SubpageContainerProps>> = (
  props
) => {
  return (
    <>
      <ErrorMessage />
      <ErrorBoundary onError={() => <PageError />}>
        <Suspense fallback={<SubpageContainerFallback {...props} />}>
          <SubpageContainerInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
